
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { phpApi } from "@/integrations/php/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Phone, CheckCircle, Clock, RefreshCw, User, Eye, AlertCircle, TrendingUp, Search, MapPin, Flame } from "lucide-react";
import PartnerDashboardHeader from "@/components/partners/PartnerDashboardHeader";
import { LeadStageType, ServiceType } from "@/context/LeadContext";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { isToday, subDays, subMonths } from "date-fns";
import LeadStatusReminderBadge from "@/components/ui-custom/LeadStatusReminderBadge";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Partner {
  id: string;
  name: string;
  phone: string;
  email: string;
  location: string;
}

interface Lead {
  id: string;
  name: string;
  phone: string;
  address: string;
  postalCode: string;
  service: ServiceType;
  timeframe: string;
  stage: LeadStageType;
  createdAt: string;
  viewed: boolean;
}

type TimeRangeType = "today" | "7d" | "30d" | "6m" | "all";

type SavedLeadView = {
  id: string;
  name: string;
  searchTerm: string;
  stageFilter: LeadStageType | "all";
  serviceFilter: ServiceType | "all";
  timeframeFilter: string | "all";
};

type LeadActivityEvent = {
  id: string;
  leadId: string;
  partnerId: string;
  source: "mini" | "stage";
  event: "view" | "call_click" | "maps_click" | "stage_changed";
  fromStage: string | null;
  toStage: string | null;
  createdAt: string;
};

type PartnerKpi = {
  key: string;
  title: string;
  value: string;
  description: string;
};

const PartnerDashboard = () => {
  const PIPELINE_COLUMNS: Array<{ key: LeadStageType; title: string; color: string; border: string }> = [
    { key: "Νέο", title: "Νέο", color: "text-purple-700", border: "border-purple-100" },
    { key: "σεΕκκρεμότητα", title: "Σε Εκκρεμότητα", color: "text-amber-700", border: "border-amber-100" },
    { key: "ήθελεΠληροφορίες", title: "Πληροφορίες", color: "text-blue-700", border: "border-blue-100" },
    { key: "έκλεισεΗΔουλειά", title: "Έκλεισε", color: "text-emerald-700", border: "border-emerald-100" },
  ];

  const { partnerId } = useParams<{ partnerId: string }>();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [miniStats, setMiniStats] = useState({
    views: 0,
    callClicks: 0,
    mapsClicks: 0,
    interactionRate: 0,
  });
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("recent");
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState<LeadStageType | "all">("all");
  const [serviceFilter, setServiceFilter] = useState<ServiceType | "all">("all");
  const [timeframeFilter, setTimeframeFilter] = useState<string | "all">("all");
  const [savedViews, setSavedViews] = useState<SavedLeadView[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRangeType>("30d");
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);
  const [updatingLeadId, setUpdatingLeadId] = useState<string | null>(null);
  const [allLeadsPage, setAllLeadsPage] = useState(1);
  const [pipelineSearchTerm, setPipelineSearchTerm] = useState("");
  const [pipelineShowHotOnly, setPipelineShowHotOnly] = useState(false);
  const [expandedTimelineLeadId, setExpandedTimelineLeadId] = useState<string | null>(null);
  const [loadingTimelineLeadId, setLoadingTimelineLeadId] = useState<string | null>(null);
  const [leadActivityMap, setLeadActivityMap] = useState<Record<string, LeadActivityEvent[]>>({});

  const buildStats = (items: Lead[]) => {
    const total = items.length;
    const completed = items.filter(lead => lead.stage === 'έκλεισεΗΔουλειά').length;
    const pending = items.filter(lead => lead.stage === 'σεΕκκρεμότητα').length;
    const newLeads = items.filter(lead => lead.stage === 'Νέο').length;
    const viewed = items.filter(lead => lead.viewed).length;

    return {
      total,
      completed,
      pending,
      new: newLeads,
      viewed,
      successRate: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  };

  const calculateLeadScore = (lead: Lead) => {
    let score = 50;

    if (lead.timeframe === "άμεσα") score += 25;
    if (lead.timeframe === "εντός3μηνών") score += 12;
    if (lead.comments && lead.comments.trim().length > 15) score += 8;
    if (lead.viewed) score += 10;
    if (lead.stage === "Νέο") score += 10;
    if (lead.stage === "σεΕκκρεμότητα") score -= 5;

    return Math.max(0, Math.min(100, score));
  };

  const fetchPartnerData = async () => {
    if (!partnerId) return;

    try {
      setIsLoading(true);
      
      const { data: partnerData, error: partnerError } = await phpApi
        .from('partners')
        .select('id, name, phone, email, location')
        .eq('id', partnerId)
        .single();
      
      if (partnerError) throw partnerError;
      setPartner(partnerData);
      
      const { data: leadsData, error: leadsError } = await phpApi
        .from('leads')
        .select('*')
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false });
      
      if (leadsError) throw leadsError;
      
      const formattedLeads = leadsData.map((lead) => ({
        id: lead.id,
        name: lead.name,
        phone: lead.phone,
        address: lead.address,
        postalCode: lead.postal_code || "",
        service: lead.service as ServiceType,
        timeframe: lead.timeframe,
        stage: (lead.stage || 'Νέο') as LeadStageType,
        createdAt: lead.created_at,
        viewed: lead.link_opened || false
      }));
      
      setLeads(formattedLeads);

      try {
        const miniStatsResponse = await fetch(`/api/get-partner-mini-stats.php?partnerId=${encodeURIComponent(partnerId)}`);
        if (miniStatsResponse.ok) {
          const miniStatsPayload = await miniStatsResponse.json();
          const first = Array.isArray(miniStatsPayload?.items) ? miniStatsPayload.items[0] : null;
          setMiniStats({
            views: Number(first?.views || 0),
            callClicks: Number(first?.callClicks || 0),
            mapsClicks: Number(first?.mapsClicks || 0),
            interactionRate: Number(first?.interactionRate || 0),
          });
        }
      } catch (miniStatsError) {
        console.error('Error fetching mini stats:', miniStatsError);
      }
      
    } catch (error) {
      console.error('Error fetching partner data:', error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η φόρτωση των δεδομένων.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkAuthorization = () => {
      const loggedInPartnerId = sessionStorage.getItem("partnerId");

      if (!loggedInPartnerId || loggedInPartnerId !== partnerId) {
        navigate("/partner/login");
        return false;
      }

      return true;
    };

    if (!checkAuthorization()) return;
    fetchPartnerData();
  }, [partnerId, navigate]);

  useEffect(() => {
    if (!autoRefresh || !partnerId) return;

    const interval = window.setInterval(() => {
      fetchPartnerData();
    }, 30000);

    return () => window.clearInterval(interval);
  }, [autoRefresh, partnerId]);

  const refreshData = () => {
    fetchPartnerData();
  };

  const updateLeadStage = async (leadId: string, stage: LeadStageType) => {
    if (updatingLeadId) return;

    const previousLeads = leads;
    const currentLead = previousLeads.find((lead) => lead.id === leadId);

    if (!currentLead || currentLead.stage === stage) return;

    const nextLeads = previousLeads.map((lead) =>
      lead.id === leadId ? { ...lead, stage } : lead
    );

    setUpdatingLeadId(leadId);
    setLeads(nextLeads);

    try {
      const { error } = await phpApi
        .from('leads')
        .update({ stage })
        .eq('id', leadId);

      if (error) {
        throw error;
      }

      if (partnerId) {
        try {
          await fetch('/api/log-lead-stage-event.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              leadId,
              partnerId,
              fromStage: currentLead.stage,
              toStage: stage,
            }),
          });
        } catch (stageLogError) {
          console.error('Failed to log stage event:', stageLogError);
        }
      }

      toast({
        title: "Η κατάσταση ενημερώθηκε",
        description: `${currentLead.name}: ${getStageLabel(stage)}`,
      });
    } catch (error) {
      setLeads(previousLeads);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η ενημέρωση της κατάστασης.",
        variant: "destructive",
      });
    } finally {
      setUpdatingLeadId(null);
      setDraggedLeadId(null);
    }
  };

  const getServiceLabel = (service: string) => {
    switch (service) {
      case "εξοικονομώ": return "Εξοικονομώ";
      case "αντλίεςΘερμότητας": return "Αντλίες Θερμότητας";
      case "φωτοβολταϊκά": return "Φωτοβολταϊκά";
      case "κλιματιστικά": return "Κλιματιστικά";
      case "φυσικόΑέριο": return "Φυσικό Αέριο";
      case "ηλιακά": return "Ηλιακά";
      case "άλλο": return "Άλλο";
      default: return service;
    }
  };

  const getStageLabel = (stage: string) => {
    switch (stage) {
      case "σεΕκκρεμότητα": return "Σε Εκκρεμότητα";
      case "έκλεισεΗΔουλειά": return "Έκλεισε η Δουλειά";
      case "ήθελεΠληροφορίες": return "Ήθελε Πληροφορίες";
      case "Νέο": return "Νέο";
      default: return stage;
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case "σεΕκκρεμότητα": return "bg-yellow-100 text-yellow-800";
      case "έκλεισεΗΔουλειά": return "bg-green-100 text-green-800";
      case "ήθελεΠληροφορίες": return "bg-blue-100 text-blue-800";
      case "Νέο": return "bg-purple-100 text-purple-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const timeFilteredLeads = useMemo(() => {
    if (timeRange === "all") return leads;

    const now = new Date();
    return leads.filter((lead) => {
      const createdAt = new Date(lead.createdAt);
      if (Number.isNaN(createdAt.getTime())) return false;

      if (timeRange === "today") return isToday(createdAt);
      if (timeRange === "7d") return createdAt >= subDays(now, 7);
      if (timeRange === "30d") return createdAt >= subDays(now, 30);
      if (timeRange === "6m") return createdAt >= subMonths(now, 6);

      return true;
    });
  }, [leads, timeRange]);

  const stats = useMemo(() => buildStats(timeFilteredLeads), [timeFilteredLeads]);

  const pipelineFilteredLeads = useMemo(() => {
    const normalizedSearch = pipelineSearchTerm.trim().toLowerCase();

    return timeFilteredLeads.filter((lead) => {
      const score = calculateLeadScore(lead);
      const hotMatch = !pipelineShowHotOnly || score >= 80;
      const searchMatch =
        !normalizedSearch ||
        lead.name.toLowerCase().includes(normalizedSearch) ||
        lead.phone.includes(normalizedSearch) ||
        lead.address.toLowerCase().includes(normalizedSearch);

      return hotMatch && searchMatch;
    });
  }, [timeFilteredLeads, pipelineSearchTerm, pipelineShowHotOnly]);

  const pipelineCounts = useMemo(() => ({
    new: pipelineFilteredLeads.filter(lead => lead.stage === "Νέο").length,
    pending: pipelineFilteredLeads.filter(lead => lead.stage === "σεΕκκρεμότητα").length,
    info: pipelineFilteredLeads.filter(lead => lead.stage === "ήθελεΠληροφορίες").length,
    closed: pipelineFilteredLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length,
  }), [pipelineFilteredLeads]);

  const kpis = useMemo<PartnerKpi[]>(() => {
    const total = stats.total;
    const info = pipelineCounts.info;
    const interactions = miniStats.callClicks + miniStats.mapsClicks;
    const viewedRate = total > 0 ? (stats.viewed / total) * 100 : 0;
    const closeRate = total > 0 ? (stats.completed / total) * 100 : 0;
    const activePipelineRate = total > 0 ? ((stats.pending + info) / total) * 100 : 0;
    const miniIntentRate = miniStats.views > 0 ? (interactions / miniStats.views) * 100 : 0;

    return [
      {
        key: "close-rate",
        title: "Lead Close Rate",
        value: `${closeRate.toFixed(1)}%`,
        description: `${stats.completed}/${total} leads έκλεισαν`,
      },
      {
        key: "active-pipeline-rate",
        title: "Active Pipeline Rate",
        value: `${activePipelineRate.toFixed(1)}%`,
        description: `${stats.pending + info}/${total} leads σε επεξεργασία`,
      },
      {
        key: "mini-intent-rate",
        title: "Mini Intent Rate",
        value: `${miniIntentRate.toFixed(1)}%`,
        description: `${interactions}/${miniStats.views} interactions από mini page`,
      },
      {
        key: "lead-view-rate",
        title: "Lead View Rate",
        value: `${viewedRate.toFixed(1)}%`,
        description: `${stats.viewed}/${total} leads έχουν ανοιχτεί`,
      },
    ];
  }, [stats, pipelineCounts.info, miniStats]);

  const filteredLeads = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return timeFilteredLeads.filter((lead) => {
      const searchMatch =
        !normalizedSearch ||
        lead.name.toLowerCase().includes(normalizedSearch) ||
        lead.phone.includes(normalizedSearch) ||
        lead.address.toLowerCase().includes(normalizedSearch);

      const stageMatch = stageFilter === "all" || lead.stage === stageFilter;
      const serviceMatch = serviceFilter === "all" || lead.service === serviceFilter;
      const timeframeMatch = timeframeFilter === "all" || lead.timeframe === timeframeFilter;

      return searchMatch && stageMatch && serviceMatch && timeframeMatch;
    });
  }, [timeFilteredLeads, searchTerm, stageFilter, serviceFilter, timeframeFilter]);

  const topPriorityLeads = useMemo(() => {
    return [...timeFilteredLeads]
      .sort((a, b) => calculateLeadScore(b) - calculateLeadScore(a))
      .slice(0, 5);
  }, [timeFilteredLeads]);

  const PAGE_SIZE = 8;
  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / PAGE_SIZE));
  const paginatedFilteredLeads = useMemo(() => {
    const start = (allLeadsPage - 1) * PAGE_SIZE;
    return filteredLeads.slice(start, start + PAGE_SIZE);
  }, [filteredLeads, allLeadsPage]);

  useEffect(() => {
    setAllLeadsPage(1);
  }, [timeRange, searchTerm, stageFilter, serviceFilter, timeframeFilter]);

  useEffect(() => {
    if (!partnerId) return;

    try {
      const raw = localStorage.getItem(`partner_dashboard_saved_views_${partnerId}`);
      if (!raw) {
        setSavedViews([]);
        return;
      }

      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setSavedViews(parsed.slice(0, 6));
      } else {
        setSavedViews([]);
      }
    } catch {
      setSavedViews([]);
    }
  }, [partnerId]);

  const saveCurrentView = () => {
    if (!partnerId) return;

    const nextIndex = savedViews.length + 1;
    const view: SavedLeadView = {
      id: `${Date.now()}`,
      name: `View ${nextIndex}`,
      searchTerm,
      stageFilter,
      serviceFilter,
      timeframeFilter,
    };

    const nextViews = [view, ...savedViews].slice(0, 6);
    setSavedViews(nextViews);
    localStorage.setItem(`partner_dashboard_saved_views_${partnerId}`, JSON.stringify(nextViews));

    toast({
      title: "Αποθηκεύτηκε φίλτρο",
      description: "Το φίλτρο προστέθηκε στις γρήγορες προβολές.",
    });
  };

  const applySavedView = (view: SavedLeadView) => {
    setSearchTerm(view.searchTerm);
    setStageFilter(view.stageFilter);
    setServiceFilter(view.serviceFilter);
    setTimeframeFilter(view.timeframeFilter);
  };

  const loadLeadActivity = async (leadId: string) => {
    if (!partnerId) return;

    if (leadActivityMap[leadId]) {
      setExpandedTimelineLeadId((prev) => (prev === leadId ? null : leadId));
      return;
    }

    setExpandedTimelineLeadId(leadId);
    setLoadingTimelineLeadId(leadId);

    try {
      const response = await fetch(`/api/get-partner-lead-activity.php?partnerId=${encodeURIComponent(partnerId)}&leadId=${encodeURIComponent(leadId)}&limit=20`);
      if (!response.ok) {
        throw new Error('Failed loading activity');
      }

      const payload = await response.json();
      const items = Array.isArray(payload?.items) ? payload.items : [];
      setLeadActivityMap((prev) => ({ ...prev, [leadId]: items }));
    } catch (activityError) {
      console.error('Error loading lead activity:', activityError);
      toast({
        title: 'Σφάλμα',
        description: 'Δεν φορτώθηκε το ιστορικό δραστηριότητας.',
        variant: 'destructive',
      });
    } finally {
      setLoadingTimelineLeadId(null);
    }
  };

  const getActivityLabel = (activity: LeadActivityEvent) => {
    if (activity.event === 'view') return 'Προβολή mini page';
    if (activity.event === 'call_click') return 'Click σε κλήση';
    if (activity.event === 'maps_click') return 'Click σε Google Maps';
    if (activity.event === 'stage_changed') {
      const fromLabel = activity.fromStage ? getStageLabel(activity.fromStage) : 'Άγνωστο';
      const toLabel = activity.toStage ? getStageLabel(activity.toStage) : 'Άγνωστο';
      return `Αλλαγή σταδίου: ${fromLabel} → ${toLabel}`;
    }

    return activity.event;
  };

  useEffect(() => {
    if (allLeadsPage > totalPages) {
      setAllLeadsPage(totalPages);
    }
  }, [allLeadsPage, totalPages]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Φόρτωση στοιχείων...</p>
        </div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <h1 className="text-2xl font-bold mb-4">Ο συνεργάτης δεν βρέθηκε</h1>
        <Button onClick={() => navigate("/partner/login")}>
          Επιστροφή στη σύνδεση
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <PartnerDashboardHeader 
        partnerName={partner.name} 
        partnerId={partner.id}
        partnerPhone={partner.phone}
        partnerEmail={partner.email}
      />
      
      <div className="container mx-auto px-4 pb-8">
        <div className="border-l-4 border-blue-500 bg-blue-50 px-4 py-3 rounded-r-lg mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-blue-800 text-sm mb-0.5">Σημαντική υπενθύμιση</h3>
              <p className="text-blue-700 text-sm">Η άμεση επικοινωνία με τους υποψήφιους πελάτες αυξάνει σημαντικά τις πιθανότητες συνεργασίας. Προτείνουμε να επικοινωνείτε εντός 1 ώρας από την ανάθεση του Lead για βέλτιστα αποτελέσματα.</p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Πίνακας Ελέγχου</h2>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={autoRefresh ? "default" : "outline"}
              onClick={() => setAutoRefresh(prev => !prev)}
            >
              Live {autoRefresh ? "ON" : "OFF"}
            </Button>
            <Button size="sm" variant="outline" onClick={refreshData} className="flex items-center gap-1">
              <RefreshCw className="h-4 w-4 mr-1" />
              Ανανέωση
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Συνολικά Leads", value: stats.total, gradient: "from-blue-500 to-indigo-600", Icon: User },
            { label: "Ολοκληρωμένα", value: stats.completed, gradient: "from-emerald-500 to-green-600", Icon: CheckCircle },
            { label: "Σε Εκκρεμότητα", value: stats.pending, gradient: "from-amber-400 to-orange-500", Icon: Clock },
            { label: "Ποσοστό Επιτυχίας", value: `${stats.successRate}%`, gradient: "from-violet-500 to-purple-600", Icon: Eye },
          ].map(({ label, value, gradient, Icon }) => (
            <Card key={label} className={`bg-gradient-to-br ${gradient} border-transparent text-white overflow-hidden relative`}>
              <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <CardContent className="p-4 relative">
                <Icon className="h-5 w-5 text-white/70 mb-2" />
                <p className="text-2xl font-bold text-white">{value}</p>
                <p className="text-xs text-white/80 mt-0.5">{label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Mini Page Views", value: miniStats.views, gradient: "from-slate-600 to-slate-700", Icon: Eye },
            { label: "Κλήσεις από Mini", value: miniStats.callClicks, gradient: "from-emerald-500 to-teal-600", Icon: Phone },
            { label: "Μετάβαση στο Maps", value: miniStats.mapsClicks, gradient: "from-blue-500 to-cyan-600", Icon: MapPin },
            { label: "Interaction Rate", value: `${miniStats.interactionRate.toFixed(1)}%`, gradient: "from-violet-500 to-purple-600", Icon: TrendingUp },
          ].map(({ label, value, gradient, Icon }) => (
            <Card key={label} className={`bg-gradient-to-br ${gradient} border-transparent text-white overflow-hidden relative`}>
              <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <CardContent className="p-4 relative">
                <Icon className="h-5 w-5 text-white/70 mb-2" />
                <p className="text-2xl font-bold text-white">{value}</p>
                <p className="text-xs text-white/80 mt-0.5">{label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Νέα", count: pipelineCounts.new,     bar: "bg-violet-500", text: "text-violet-700", bg: "bg-violet-50",  border: "border-violet-100" },
            { label: "Εκκρεμότητα", count: pipelineCounts.pending, bar: "bg-amber-500",  text: "text-amber-700",  bg: "bg-amber-50",   border: "border-amber-100" },
            { label: "Πληροφορίες", count: pipelineCounts.info,    bar: "bg-blue-500",   text: "text-blue-700",   bg: "bg-blue-50",    border: "border-blue-100" },
            { label: "Κλεισμένα",  count: pipelineCounts.closed,  bar: "bg-emerald-500",text: "text-emerald-700",bg: "bg-emerald-50", border: "border-emerald-100" },
          ].map(({ label, count, bar, text, bg, border }) => {
            const total = pipelineCounts.new + pipelineCounts.pending + pipelineCounts.info + pipelineCounts.closed;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <div key={label} className={`p-3 rounded-xl border ${bg} ${border}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-xs font-semibold ${text}`}>{label}</span>
                  <span className={`text-lg font-bold ${text}`}>{count}</span>
                </div>
                <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
                  <div className={`h-full ${bar} rounded-full`} style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">{pct}%</p>
              </div>
            );
          })}
        </div>

        <div className="mb-6">
          <Tabs value={timeRange} onValueChange={(value) => setTimeRange(value as TimeRangeType)}>
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-5">
              <TabsTrigger value="today">Σήμερα</TabsTrigger>
              <TabsTrigger value="7d">7 Μέρες</TabsTrigger>
              <TabsTrigger value="30d">Μήνας</TabsTrigger>
              <TabsTrigger value="6m">6μηνο</TabsTrigger>
              <TabsTrigger value="all">Όλα</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <Card className="mb-6 border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">KPI Απόδοσης</CardTitle>
            <p className="text-sm text-slate-500">Τα KPI υπολογίζονται με βάση το επιλεγμένο χρονικό tab.</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {kpis.map((kpi, idx) => {
                const gradients = ["from-blue-500 to-indigo-600", "from-emerald-500 to-teal-600", "from-violet-500 to-purple-600", "from-amber-400 to-orange-500"];
                return (
                  <div key={kpi.key} className={`rounded-xl p-4 bg-gradient-to-br ${gradients[idx]} text-white relative overflow-hidden`}>
                    <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <p className="text-xs text-white/70 relative">{kpi.title}</p>
                    <p className="text-2xl font-bold text-white mt-1 relative">{kpi.value}</p>
                    <p className="text-xs text-white/60 mt-1 relative">{kpi.description}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6 border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Pipeline Leads</CardTitle>
            <p className="text-sm text-slate-500">
              Σύρε lead σε άλλη στήλη ή άλλαξε στάδιο από το dropdown για άμεση ενημέρωση.
            </p>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-col md:flex-row md:items-center gap-2">
              <div className="relative md:max-w-sm w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={pipelineSearchTerm}
                  onChange={(e) => setPipelineSearchTerm(e.target.value)}
                  placeholder="Αναζήτηση στο pipeline..."
                  className="pl-10 bg-slate-50 border-slate-200"
                />
              </div>
              <Button
                size="sm"
                variant={pipelineShowHotOnly ? "default" : "outline"}
                onClick={() => setPipelineShowHotOnly((prev) => !prev)}
                className="md:w-auto w-full"
              >
                <Flame className="h-4 w-4 mr-1" />
                Μόνο Hot Leads (80+)
              </Button>
              <p className="text-xs text-slate-500 md:ml-auto">
                Προβολή {pipelineFilteredLeads.length} lead(s)
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {PIPELINE_COLUMNS.map((column) => {
                const columnLeads = pipelineFilteredLeads.filter((lead) => lead.stage === column.key);

                return (
                  <div
                    key={column.key}
                    className={`rounded-xl border bg-white ${column.border}`}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => {
                      if (draggedLeadId) {
                        void updateLeadStage(draggedLeadId, column.key);
                      }
                    }}
                  >
                    <div className={`px-3 py-2.5 border-b flex items-center justify-between ${
                      column.key === 'Νέο' ? 'bg-violet-50 border-violet-100' :
                      column.key === 'σεΕκκρεμότητα' ? 'bg-amber-50 border-amber-100' :
                      column.key === 'ήθελεΠληροφορίες' ? 'bg-blue-50 border-blue-100' :
                      'bg-emerald-50 border-emerald-100'
                    }`}>
                      <p className={`text-sm font-semibold ${column.color}`}>{column.title}</p>
                      <Badge variant="outline" className={`text-xs ${column.color}`}>{columnLeads.length}</Badge>
                    </div>

                    <div className="p-3 space-y-3 min-h-[180px]">
                      {columnLeads.length === 0 && (
                        <p className="text-xs text-slate-400">Δεν υπάρχουν leads με τα τρέχοντα φίλτρα</p>
                      )}

                      {columnLeads.map((lead) => (
                        <div
                          key={lead.id}
                          draggable={updatingLeadId !== lead.id}
                          onDragStart={() => setDraggedLeadId(lead.id)}
                          onDragEnd={() => setDraggedLeadId(null)}
                          className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow cursor-grab"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-slate-800 truncate">{lead.name}</p>
                              <p className="text-xs text-slate-500 truncate">{lead.phone}</p>
                            </div>
                            <Badge variant="outline" className={`text-[10px] font-bold border ${
                              calculateLeadScore(lead) >= 80 ? 'text-emerald-700 border-emerald-200 bg-emerald-50' :
                              calculateLeadScore(lead) >= 60 ? 'text-amber-700 border-amber-200 bg-amber-50' :
                              'text-slate-600 border-slate-200 bg-slate-50'
                            }`}>
                              {calculateLeadScore(lead)}
                            </Badge>
                          </div>

                          <p className="text-xs text-slate-500 mt-2 truncate">{lead.address}</p>

                          <div className="mt-2 space-y-2">
                            <Select
                              value={lead.stage}
                              onValueChange={(value) => void updateLeadStage(lead.id, value as LeadStageType)}
                              disabled={updatingLeadId === lead.id}
                            >
                              <SelectTrigger className="h-8 text-xs bg-white">
                                <SelectValue placeholder="Αλλαγή σταδίου" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Νέο">Νέο</SelectItem>
                                <SelectItem value="σεΕκκρεμότητα">Σε εκκρεμότητα</SelectItem>
                                <SelectItem value="ήθελεΠληροφορίες">Ήθελε πληροφορίες</SelectItem>
                                <SelectItem value="έκλεισεΗΔουλειά">Έκλεισε η δουλειά</SelectItem>
                              </SelectContent>
                            </Select>

                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-slate-500">
                                {format(new Date(lead.createdAt), 'dd/MM/yyyy', { locale: el })}
                              </span>
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(`/lead/${lead.id}`)}>
                                Προβολή
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        <Tabs defaultValue="recent" value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-4">
            <TabsTrigger value="recent">Top Προτεραιότητα</TabsTrigger>
            <TabsTrigger value="all">Οι Πελάτες μου</TabsTrigger>
          </TabsList>
          
          <TabsContent value="recent">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Πρόσφατες αναθέσεις</CardTitle>
              </CardHeader>
              <CardContent>
                {topPriorityLeads.length > 0 ? (
                  <div className="space-y-4">
                    {topPriorityLeads.map((lead) => (
                      <div key={lead.id} className="rounded-xl border border-slate-100 bg-white hover:border-slate-200 transition-all">
                        <div className="flex items-center gap-4 p-4">
                          {/* Score ring */}
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white shadow-sm bg-gradient-to-br ${
                            calculateLeadScore(lead) >= 80 ? 'from-emerald-500 to-teal-600' :
                            calculateLeadScore(lead) >= 60 ? 'from-amber-400 to-orange-500' :
                            'from-slate-400 to-slate-500'
                          }`}>
                            {calculateLeadScore(lead)}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <p className="font-semibold text-slate-900 text-sm truncate">{lead.name}</p>
                              <LeadStatusReminderBadge lastUpdated={lead.createdAt} stage={lead.stage} />
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-400">
                              <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>
                              <span className="truncate">{lead.address}</span>
                            </div>
                            {/* Score bar */}
                            <div className="flex items-center gap-2 mt-1.5">
                              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${calculateLeadScore(lead) >= 80 ? 'bg-emerald-500' : calculateLeadScore(lead) >= 60 ? 'bg-amber-500' : 'bg-slate-400'}`}
                                  style={{ width: `${calculateLeadScore(lead)}%` }} />
                              </div>
                            </div>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1.5">
                              {getServiceLabel(lead.service)}
                            </span>
                          </div>

                          <div className="flex flex-col items-end gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStageColor(lead.stage)}`}>
                              {getStageLabel(lead.stage)}
                            </span>
                            <span className="text-[10px] text-slate-400">{format(new Date(lead.createdAt), 'dd/MM/yy', { locale: el })}</span>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => void loadLeadActivity(lead.id)}>Ιστορικό</Button>
                            <Button size="sm" className="h-8 text-xs" onClick={() => navigate(`/lead/${lead.id}`)}>Προβολή</Button>
                          </div>
                        </div>

                        {expandedTimelineLeadId === lead.id && (
                          <div className="mx-4 mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs font-medium text-slate-700 mb-2">Activity Timeline</p>

                            {loadingTimelineLeadId === lead.id ? (
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Φόρτωση δραστηριότητας...
                              </div>
                            ) : (leadActivityMap[lead.id] || []).length > 0 ? (
                              <div className="space-y-2">
                                {(leadActivityMap[lead.id] || []).map((activity) => (
                                  <div key={activity.id} className="flex items-start justify-between gap-2 text-xs">
                                    <p className="text-slate-700">{getActivityLabel(activity)}</p>
                                    <span className="text-slate-500 whitespace-nowrap">
                                      {format(new Date(activity.createdAt), 'dd/MM HH:mm', { locale: el })}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-slate-500">Δεν υπάρχει activity για αυτό το lead.</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Δεν υπάρχουν αναθέσεις ακόμα</p>
                  </div>
                )}
                
                {timeFilteredLeads.length > 5 && (
                  <div className="mt-4 text-center">
                    <Button 
                      variant="outline" 
                      onClick={() => setActiveTab("all")}
                    >
                      Προβολή Όλων ({timeFilteredLeads.length})
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Οι πελάτες μου</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4 space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Αναζήτηση πελάτη, τηλεφώνου, διεύθυνσης..."
                      className="pl-10 bg-slate-50 border-slate-200"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Select value={serviceFilter} onValueChange={(value) => setServiceFilter(value as ServiceType | "all") }>
                      <SelectTrigger className="bg-slate-50 border-slate-200">
                        <SelectValue placeholder="Φίλτρο υπηρεσίας" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Όλες οι υπηρεσίες</SelectItem>
                        <SelectItem value="εξοικονομώ">Εξοικονομώ</SelectItem>
                        <SelectItem value="αντλίεςΘερμότητας">Αντλίες Θερμότητας</SelectItem>
                        <SelectItem value="φωτοβολταϊκά">Φωτοβολταϊκά</SelectItem>
                        <SelectItem value="κλιματιστικά">Κλιματιστικά</SelectItem>
                        <SelectItem value="φυσικόΑέριο">Φυσικό Αέριο</SelectItem>
                        <SelectItem value="ηλιακά">Ηλιακά</SelectItem>
                        <SelectItem value="άλλο">Άλλο</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={timeframeFilter} onValueChange={setTimeframeFilter}>
                      <SelectTrigger className="bg-slate-50 border-slate-200">
                        <SelectValue placeholder="Φίλτρο χρονικού ορίζοντα" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Όλοι οι χρονικοί ορίζοντες</SelectItem>
                        <SelectItem value="άμεσα">Άμεσα</SelectItem>
                        <SelectItem value="εντός3μηνών">Εντός 3 μηνών</SelectItem>
                        <SelectItem value="δενΞέρω">Δεν ξέρω</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="secondary" onClick={saveCurrentView}>Αποθήκευση view</Button>
                    {savedViews.map((view) => (
                      <Button key={view.id} size="sm" variant="outline" onClick={() => applySavedView(view)}>
                        {view.name}
                      </Button>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { value: "all", label: "Όλα" },
                      { value: "Νέο", label: "Νέα" },
                      { value: "σεΕκκρεμότητα", label: "Εκκρεμότητα" },
                      { value: "ήθελεΠληροφορίες", label: "Πληροφορίες" },
                      { value: "έκλεισεΗΔουλειά", label: "Κλεισμένα" },
                    ].map(({ value, label }) => (
                      <button
                        key={value}
                        onClick={() => setStageFilter(value as LeadStageType | "all")}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          stageFilter === value ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {paginatedFilteredLeads.length > 0 ? (
                  <div className="space-y-4">
                    {paginatedFilteredLeads.map((lead) => (
                      <div key={lead.id} className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 bg-white hover:bg-slate-50 hover:border-slate-200 transition-all">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="font-semibold text-slate-900 text-sm truncate">{lead.name}</p>
                            <LeadStatusReminderBadge lastUpdated={lead.createdAt} stage={lead.stage} />
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-400">
                            <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>
                            <span className="truncate">{lead.address}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {getServiceLabel(lead.service)}
                            </span>
                            <span className="text-[10px] text-slate-400">{format(new Date(lead.createdAt), 'dd/MM/yy', { locale: el })}</span>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2 min-w-[120px]">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStageColor(lead.stage)}`}>
                            {getStageLabel(lead.stage)}
                          </span>
                          <div className="flex items-center gap-1.5 w-full">
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${calculateLeadScore(lead) >= 80 ? 'bg-emerald-500' : calculateLeadScore(lead) >= 60 ? 'bg-amber-500' : 'bg-slate-400'}`}
                                style={{ width: `${calculateLeadScore(lead)}%` }} />
                            </div>
                            <span className={`text-[10px] font-bold ${calculateLeadScore(lead) >= 80 ? 'text-emerald-600' : calculateLeadScore(lead) >= 60 ? 'text-amber-600' : 'text-slate-500'}`}>
                              {calculateLeadScore(lead)}
                            </span>
                          </div>
                        </div>

                        <Button
                          size="sm"
                          className="h-8 text-xs flex-shrink-0"
                          onClick={() => navigate(`/lead/${lead.id}`)}
                        >
                          Προβολή
                        </Button>
                      </div>
                    ))}

                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-2">
                        <p className="text-xs text-slate-500">
                          Σελίδα {allLeadsPage} από {totalPages}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={allLeadsPage === 1}
                            onClick={() => setAllLeadsPage((page) => Math.max(1, page - 1))}
                          >
                            Προηγούμενη
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={allLeadsPage === totalPages}
                            onClick={() => setAllLeadsPage((page) => Math.min(totalPages, page + 1))}
                          >
                            Επόμενη
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Δεν βρέθηκαν πελάτες για τα φίλτρα</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
};

export default PartnerDashboard;
